<template>
  <div class="report-list-page">
    <section class="page-section quick-entry">
      <div class="quick-copy">
        <h2 class="section-title">我的周报</h2>
        <div class="quick-summary">
          <span class="summary-pill">共 {{ pager.total }} 篇</span>
          <span class="summary-pill">上传入口常驻</span>
        </div>
      </div>

      <div class="quick-actions">
        <el-button type="primary" size="large" @click="goCreate('word')">
          <el-icon><UploadFilled /></el-icon>
          上传 Word 周报
        </el-button>
        <el-button size="large" @click="goCreate()">
          <el-icon><EditPen /></el-icon>
          在线新建
        </el-button>
        <el-button size="large" plain @click="loadList">
          <el-icon><RefreshRight /></el-icon>
          刷新列表
        </el-button>
      </div>
    </section>

    <section class="page-section">
      <div class="filter-bar">
        <el-input
          v-model="filters.keyword"
          class="filter-field filter-keyword"
          placeholder="搜索标题、周次、摘要或 Word 文件名"
          clearable
          @keyup.enter="handleSearch"
        />
        <el-select
          v-model="filters.status"
          class="filter-field filter-status"
          placeholder="全部状态"
          clearable
        >
          <el-option label="草稿" :value="0" />
          <el-option label="待审核" :value="1" />
          <el-option label="已发布" :value="2" />
          <el-option label="已驳回" :value="3" />
        </el-select>
        <el-button @click="handleSearch">查询</el-button>
        <el-button @click="handleReset">重置</el-button>
      </div>

      <el-table :data="list" v-loading="loading" row-key="id" empty-text="还没有周报，先上传一篇">
        <template #empty>
          <div class="empty-state">
            <div class="empty-title">还没有周报</div>
            <div class="empty-actions">
              <el-button type="primary" @click="goCreate('word')">上传 Word 周报</el-button>
              <el-button @click="goCreate()">在线新建</el-button>
            </div>
          </div>
        </template>

        <el-table-column prop="report_week" label="周次" width="120" />

        <el-table-column label="标题" min-width="360">
          <template #default="{ row }">
            <div class="title-cell">
              <div class="title-row">
                <span class="title-text">{{ row.title }}</span>
                <el-tag :type="row.source_document ? 'success' : 'info'" effect="plain" size="small">
                  {{ row.source_document ? 'Word 导入' : '在线编辑' }}
                </el-tag>
              </div>
              <div v-if="row.source_document?.file_name" class="muted title-file">
                {{ row.source_document.file_name }}
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="status" label="状态" width="120">
          <template #default="{ row }">
            <el-tag :type="REPORT_STATUS_TYPE_MAP[row.status]">
              {{ REPORT_STATUS_TEXT_MAP[row.status] }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="review_comment" label="审核反馈" min-width="220" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.review_comment || (row.status === 2 ? '审核通过' : '-') }}
          </template>
        </el-table-column>

        <el-table-column prop="updated_at" label="更新时间" width="170" />

        <el-table-column label="操作" width="220" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="router.push(`/reports/${row.id}`)">继续编辑</el-button>
            <el-button link @click="openPreview(row)">预览</el-button>
            <el-button link type="danger" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          background
          layout="total, prev, pager, next, sizes"
          :current-page="pager.page"
          :page-size="pager.pageSize"
          :page-sizes="[10, 20, 50]"
          :total="pager.total"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </section>

    <el-dialog v-model="previewVisible" title="周报预览" width="min(860px, calc(100vw - 32px))">
      <div v-if="previewReport" class="preview">
        <h3>{{ previewReport.title }}</h3>

        <div class="preview-meta">
          <el-tag :type="REPORT_STATUS_TYPE_MAP[previewReport.status]">
            {{ REPORT_STATUS_TEXT_MAP[previewReport.status] }}
          </el-tag>
          <el-tag v-if="previewReport.source_document" type="success" effect="plain">Word 原件</el-tag>
          <span class="muted">{{ previewReport.updated_at }}</span>
          <el-button link type="danger" :loading="deletingReport" @click="handleDelete(previewReport)">
            删除周报
          </el-button>
        </div>

        <p v-if="previewReport.review_comment" class="preview-note">
          审核反馈：{{ previewReport.review_comment }}
        </p>

        <div v-if="previewReport.source_document" class="source-doc">
          <div class="source-main">
            <el-tag type="success">Word</el-tag>
            <a
              :href="previewReport.source_document.file_url"
              target="_blank"
              rel="noreferrer"
              class="source-link"
            >
              {{ previewReport.source_document.file_name }}
            </a>
          </div>
          <div class="source-meta">
            <span class="muted">{{ formatFileSize(previewReport.source_document.file_size) }}</span>
            <span class="muted">{{ previewReport.source_document.uploaded_at }}</span>
          </div>
        </div>

        <div class="preview-body" v-html="sanitizedPreviewContent"></div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { EditPen, RefreshRight, UploadFilled } from '@element-plus/icons-vue';
import { useRouter } from 'vue-router';

import { deleteMyReport, getMyReportList } from '../../api/report';
import { REPORT_STATUS_TEXT_MAP, REPORT_STATUS_TYPE_MAP } from '../../constants/report';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const router = useRouter();

const loading = ref(false);
const deletingReport = ref(false);
const list = ref([]);
const previewVisible = ref(false);
const previewReport = ref(null);
const sanitizedPreviewContent = computed(() => sanitizeHtml(previewReport.value?.content || ''));

const filters = reactive({
  keyword: '',
  status: ''
});

const pager = reactive({
  page: 1,
  pageSize: 10,
  total: 0
});

const formatFileSize = (size) => {
  const value = Number(size) || 0;
  if (!value) return '0 B';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const loadList = async () => {
  loading.value = true;
  try {
    const result = await getMyReportList({
      page: pager.page,
      pageSize: pager.pageSize,
      status: filters.status,
      keyword: filters.keyword
    });
    list.value = result.data.list || [];
    pager.total = result.data.total || 0;
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '获取周报列表失败');
  } finally {
    loading.value = false;
  }
};

const goCreate = (mode = 'editor') => {
  const query = mode === 'word' ? { mode: 'word' } : {};
  router.push({ path: '/reports/new', query });
};

const handleSearch = () => {
  pager.page = 1;
  loadList();
};

const handleReset = () => {
  filters.keyword = '';
  filters.status = '';
  pager.page = 1;
  pager.pageSize = 10;
  loadList();
};

const handlePageChange = (page) => {
  pager.page = page;
  loadList();
};

const handleSizeChange = (pageSize) => {
  pager.page = 1;
  pager.pageSize = pageSize;
  loadList();
};

const openPreview = (row) => {
  previewReport.value = row;
  previewVisible.value = true;
};

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(`确定删除《${row.title}》吗？`, '删除确认', {
      type: 'warning'
    });
    deletingReport.value = true;
    await deleteMyReport(row.id);
    if (previewReport.value?.id === row.id) {
      previewVisible.value = false;
      previewReport.value = null;
    }
    ElMessage.success('删除成功');
    if (list.value.length === 1 && pager.page > 1) {
      pager.page -= 1;
    }
    await loadList();
  } catch (error) {
    if (error === 'cancel') return;
    ElMessage.error(error.response?.data?.message || '删除失败');
  } finally {
    deletingReport.value = false;
  }
};

onMounted(loadList);
</script>

<style scoped>
.report-list-page {
  display: grid;
  gap: 20px;
}

.quick-entry {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.quick-copy {
  min-width: 0;
}

.quick-summary {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.summary-pill {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 13px;
}

.quick-actions,
.filter-bar {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.quick-actions {
  justify-content: flex-end;
}

.filter-bar {
  margin-bottom: 16px;
}

.filter-field {
  width: 100%;
}

.filter-keyword {
  flex: 1 1 320px;
  max-width: 420px;
}

.filter-status {
  flex: 0 0 140px;
  max-width: 160px;
}

.title-cell {
  display: grid;
  gap: 6px;
}

.title-row,
.preview-meta,
.source-main,
.source-meta,
.empty-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.title-text {
  font-weight: 600;
  word-break: break-word;
}

.title-file {
  font-size: 12px;
}

.pager {
  margin-top: 18px;
  display: flex;
  justify-content: flex-end;
}

.empty-state {
  padding: 12px 0;
  display: grid;
  gap: 12px;
  justify-items: center;
}

.empty-title {
  color: #4b5563;
}

.preview h3 {
  margin: 0;
  font-size: 24px;
  word-break: break-word;
}

.preview-meta {
  margin: 12px 0 16px;
}

.preview-note {
  margin: 0 0 16px;
  color: #4b5563;
  line-height: 1.7;
  word-break: break-word;
}

.source-doc {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}

.source-link {
  color: #2563eb;
  word-break: break-word;
}

.preview-body {
  line-height: 1.9;
  word-break: break-word;
  overflow: hidden;
}

@media (max-width: 768px) {
  .quick-actions,
  .filter-bar {
    width: 100%;
  }

  .filter-keyword,
  .filter-status {
    max-width: none;
  }
}
</style>
